import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Markdown } from "@/components/Markdown";

/** The LimoLinkX blog — public, so it works as a front door as well as a reading list. */
export default function Blog() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    api.get(slug ? `/public/blog/${slug}` : "/public/blog?include=all")
      .then((res) => {
        setData(res.data);
        if (res.data?.post?.title) document.title = `${res.data.post.title} — LimoLinkX`;
      })
      .catch(() => setError(slug ? "That post is not here." : "The blog is not available."));
  }, [slug]);

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink-900 px-6 text-center">
        <div>
          <p className="text-sm text-slate-400">{error}</p>
          <Link to="/blog" className="mt-4 inline-block text-sm text-gold hover:underline">
            All posts
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="grid min-h-screen place-items-center bg-ink-900">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="blog-page" className="min-h-screen bg-ink-900 px-5 py-14 sm:px-10 lg:px-20">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/" data-testid="blog-home" className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
            LimoLinkX
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/blog" className="text-slate-400 hover:text-gold">Blog</Link>
            <Link to="/plans" className="text-slate-400 hover:text-gold">Pricing</Link>
            <Link data-testid="blog-signup" to="/signup"
              className="rounded-full bg-gold px-4 py-2 text-sm font-semibold text-ink-900 transition-transform duration-200 hover:scale-[1.03]">
              Start free
            </Link>
          </nav>
        </div>

        {data.post ? (
          <article className="mt-10">
            <Link data-testid="blog-back" to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-gold">
              <ArrowLeft className="h-3.5 w-3.5" /> All posts
            </Link>
            <h1 className="mt-6 font-display text-4xl leading-tight text-slate-50 sm:text-5xl">
              {data.post.title}
            </h1>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">
              {(data.post.published_at || "").slice(0, 10)}
            </p>
            {data.post.cover_url && (
              <img data-testid="blog-cover-image" src={data.post.cover_url} alt=""
                className="mt-8 aspect-[16/9] w-full rounded-2xl object-cover" />
            )}
            <div className="mt-8">
              <Markdown text={data.post.body} />
            </div>
          </article>
        ) : (
          <>
            <h1 className="mt-10 font-display text-4xl leading-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Notes on running a car service.
            </h1>
            <div className="mt-14 space-y-3">
              {data.posts.map((post) => (
                <Link key={post.id} data-testid={`blog-card-${post.slug}`} to={post.path || `/blog/${post.slug}`}
                  className="block overflow-hidden rounded-2xl border border-ink-600 bg-ink-800/60 transition-colors duration-200 hover:border-gold/50">
                  {post.cover_url && (
                    <img src={post.cover_url} alt="" data-testid={`blog-card-cover-${post.slug}`}
                      className="aspect-[16/9] w-full object-cover" />
                  )}
                  <div className="p-6">
                    {post.source && (
                      <p data-testid={`blog-card-source-${post.slug}`} className="font-mono text-[10px] uppercase tracking-widest text-gold/70">
                        {post.source}
                      </p>
                    )}
                    <p className="mt-1 font-display text-xl text-slate-100">{post.title}</p>
                    <p className="mt-2 text-sm text-slate-400">{post.excerpt}</p>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {(post.published_at || "").slice(0, 10)}
                    </p>
                  </div>
                </Link>
              ))}
              {data.posts.length === 0 && (
                <p data-testid="blog-none" className="text-sm text-slate-500">
                  Nothing published yet.
                </p>
              )}
            </div>
          </>
        )}

        <footer data-testid="blog-footer" className="mt-20 border-t border-ink-600 pt-8">
          <p className="font-display text-xl text-slate-100">
            Run your car service on LimoLinkX.
          </p>
          <p className="mt-2 max-w-xl text-sm text-slate-400">
            Reservations, dispatch, chauffeur and passenger apps, invoicing — the software black car
            operators run their day on.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm">
            <Link data-testid="blog-footer-signup" to="/signup"
              className="rounded-full bg-gold px-5 py-2.5 font-semibold text-ink-900">
              Start free
            </Link>
            <Link data-testid="blog-footer-home" to="/" className="text-slate-400 hover:text-gold">
              How it works
            </Link>
            <Link to="/plans" className="text-slate-400 hover:text-gold">Plans &amp; pricing</Link>
            <Link to="/blog" className="text-slate-400 hover:text-gold">All posts</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
